
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { Treatment, SiteSettings } from '../types';

interface HomeProps {
  treatments: Treatment[];
  settings: SiteSettings;
}

const Home: React.FC<HomeProps> = ({ treatments, settings }) => {
  return (
    <div className="w-full">
      <Helmet>
        <title>Esthetische Geneeskunde Antwerpen | Dr. An De Cuyper</title>
        <meta name="description" content="Natuurlijke esthetische behandelingen bij Dr. An De Cuyper in Antwerpen, Edegem, Hoboken en Burcht. Persoonlijk advies met medische expertise." />
        <link rel="canonical" href="https://andecuyper.be/" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center overflow-hidden bg-background-dark dark-theme">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://res.cloudinary.com/dbtohvvbj/image/upload/f_auto,q_auto,w_1200/v1766150991/Front_4__776X576_bfyevs.png" 
            alt="Clinic Hero" 
            title="Esthetische praktijk Dr. An De Cuyper"
            className="w-full h-full object-cover grayscale brightness-50"
            fetchPriority="high"
            width="1200"
            height="800"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-background-dark/80 via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full text-right">
          <div className="max-w-2xl ml-auto">
            <span className="text-primary text-xs font-bold uppercase tracking-[0.4em] mb-4 block">Esthetische Geneeskunde</span>
            <h1 className="font-display text-5xl md:text-8xl text-white mb-6 leading-tight whitespace-pre-line">
              {settings.heroTitle.split(' ').map((word, i) => (
                <React.Fragment key={i}>
                  {i % 2 === 1 ? <i className="text-primary italic font-serif font-light">{word} </i> : <span>{word} </span>}
                </React.Fragment>
              ))}
            </h1>
            <p className="text-gray-300 text-lg md:text-xl font-light mb-10 max-w-md ml-auto leading-relaxed border-r border-white/20 pr-6">
              {settings.heroSubtitle}
            </p>
            <div className="flex gap-6 justify-end">
              <Link to="/botox-antwerpen" className="bg-primary text-black px-10 py-4 text-xs font-bold uppercase tracking-widest hover:bg-white transition-all">
                Botox Antwerpen
              </Link>
              <Link to="/fillers-antwerpen" className="text-white hover:text-primary py-4 text-xs font-bold uppercase tracking-widest flex items-center gap-2 group">
                Volume & Fillers <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Treatments */}
      <section className="py-24 bg-theme-surface border-b border-theme-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-primary text-xs font-bold uppercase tracking-[0.3em]">Top Behandelingen</span>
            <h2 className="font-display text-4xl text-theme-text mt-2">Onze Specialisaties</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link to="/botox-antwerpen" className="group block bg-theme-bg border border-theme-border rounded-lg overflow-hidden hover:border-primary/50 transition-all shadow-sm hover:shadow-xl">
              <div className="aspect-[4/3] overflow-hidden">
                <img src="https://res.cloudinary.com/dbtohvvbj/image/upload/f_auto,q_auto,w_600/v1766150991/Front_4__776X576_bfyevs.png" alt="Botox Antwerpen" title="Botoxbehandeling in Antwerpen" className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-105 group-hover:grayscale-0" />
              </div>
              <div className="p-8">
                <span className="text-primary text-[10px] uppercase font-bold tracking-widest">Rimpels</span>
                <h3 className="text-xl text-theme-text font-display mt-2 mb-4 group-hover:text-primary transition-colors">Botox Antwerpen</h3>
                <p className="text-theme-muted text-sm line-clamp-2 mb-6">Hoogwaardige spierontspanners voor een natuurlijke en uitgeruste blik.</p>
                <div className="text-xs font-bold text-theme-text uppercase tracking-widest group-hover:text-primary">Ontdek meer →</div>
              </div>
            </Link>

            <Link to="/fillers-antwerpen" className="group block bg-theme-bg border border-theme-border rounded-lg overflow-hidden hover:border-primary/50 transition-all shadow-sm hover:shadow-xl">
              <div className="aspect-[4/3] overflow-hidden">
                <img src="https://res.cloudinary.com/dbtohvvbj/image/upload/v1766744175/MD_and_patient_froq58.png" alt="Fillers Antwerpen" title="Fillerbehandeling in Antwerpen" className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-105 group-hover:grayscale-0" />
              </div>
              <div className="p-8">
                <span className="text-primary text-[10px] uppercase font-bold tracking-widest">Volume</span>
                <h3 className="text-xl text-theme-text font-display mt-2 mb-4 group-hover:text-primary transition-colors">Fillers Antwerpen</h3>
                <p className="text-theme-muted text-sm line-clamp-2 mb-6">Herstel volume in lippen, jukbeenderen en kaaklijn met hyaluronzuur.</p>
                <div className="text-xs font-bold text-theme-text uppercase tracking-widest group-hover:text-primary">Ontdek meer →</div>
              </div>
            </Link>

            <Link to="/profhilo-antwerpen" className="group block bg-theme-bg border border-theme-border rounded-lg overflow-hidden hover:border-primary/50 transition-all shadow-sm hover:shadow-xl">
              <div className="aspect-[4/3] overflow-hidden">
                <img src="https://res.cloudinary.com/dbtohvvbj/image/upload/f_auto,q_auto,w_1200/v1766150991/Front_4__776X576_bfyevs.png" alt="Profhilo Antwerpen" title="Profhilo behandeling in Antwerpen" className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-105 group-hover:grayscale-0" />
              </div>
              <div className="p-8">
                <span className="text-primary text-[10px] uppercase font-bold tracking-widest">Huid</span>
                <h3 className="text-xl text-theme-text font-display mt-2 mb-4 group-hover:text-primary transition-colors">Profhilo Antwerpen</h3>
                <p className="text-theme-muted text-sm line-clamp-2 mb-6">Krachtige bioremodeling voor huidverstrakking en een natuurlijke gloed.</p>
                <div className="text-xs font-bold text-theme-text uppercase tracking-widest group-hover:text-primary">Ontdek meer →</div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* About Doctor Section */}
      <section className="py-24 bg-theme-bg relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image Column */}
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 border border-primary/20 rounded-[2rem] translate-x-4 translate-y-4"></div>
              <div className="aspect-[4/5] rounded-[2rem] overflow-hidden relative shadow-2xl">
                <img 
                  src="https://res.cloudinary.com/dbtohvvbj/image/upload/f_auto,q_auto,w_800/v1766744175/MD_and_patient_froq58.png" 
                  alt="Dr. An De Cuyper" 
                  title="Dr. An De Cuyper"
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000"
                  loading="lazy"
                  width="800"
                  height="1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60"></div>
                <div className="absolute bottom-8 left-8 text-white">
                  <p className="font-display text-2xl">MD. An De Cuyper</p>
                  <p className="text-primary text-xs font-bold uppercase tracking-widest">Arts - Chirurg</p>
                </div>
              </div>
            </div>

            {/* Text Column */}
            <div className="order-1 lg:order-2 space-y-8">
              <div>
                <span className="text-primary text-xs font-bold uppercase tracking-[0.3em] mb-4 block">De Arts</span>
                <h2 className="font-display text-4xl md:text-5xl text-theme-text leading-tight">
                  Ervaring & <br/>
                  <span className="text-theme-muted italic">Esthetisch Inzicht</span>
                </h2>
              </div>
              
              <div className="space-y-6 text-theme-muted text-lg font-light leading-relaxed">
                <p>
                  Dr. An De Cuyper is arts chirurg. Na een carrière in de orthopedische chirurgie in binnen – en buitenland, volgde een aantal mandaten in het management van enkele ziekenhuizen in België.
                </p>
                <p>
                  Sinds 2019 is zij werkzaam als esthetisch arts. Zij streeft naar het bekomen van natuurlijk ogende frisse en jonger uitziende patiënten door het gebruik van spierontspanners, fillers, draadjes en laserapparaten.
                </p>
                <p>
                  In onze praktijk in de regio Antwerpen staat de persoonlijke consultatie altijd centraal. Tijdens dit intakegesprek neemt Dr. An De Cuyper uitgebreid de tijd om uw anatomie, huidkwaliteit en specifieke esthetische wensen grondig te analyseren. Elk gelaat is immers uniek en vraagt om een doordacht, op maat gemaakt behandelplan waarin medische expertise en esthetische balans hand in hand gaan.
                </p>
                <p>
                  De filosofie van de praktijk is geworteld in het behalen van subtiele, natuurlijk ogende resultaten. Het doel van elke behandeling is niet het veranderen van uw gelaatstrekken, maar het accentueren van uw eigen schoonheid en het herstellen van een frisse, vitale uitstraling. Binnen de esthetische geneeskunde wordt gewerkt met hoogwaardige, geteste producten en geavanceerde behandelmethoden die voldoen aan de strengste medische kwaliteitsnormen.
                </p>
                <p>
                  Of u nu kiest voor preventieve huidverbetering, het verzachten van rimpels of het herstellen van gelaatsvolume, u kunt rekenen op deskundige begeleiding in een professionele en rustgevende omgeving. Onze vestigingen in Antwerpen, Edegem, Hoboken en Burcht bieden een vlot bereikbare locatie voor patiënten die waarde hechten aan medische zorgvuldigheid en een discreet advies.
                </p>
                <p>
                  Door continue bijscholing en het volgen van de nieuwste internationale inzichten in de esthetische geneeskunde, garandeert Dr. An De Cuyper verantwoorde behandelingen die afgestemd zijn op uw persoonlijke behoeften en levensfase.
                </p>
              </div>

              <div className="pt-8 flex gap-8 border-t border-theme-border mt-8">
                <div>
                  <span className="block text-3xl font-display text-primary">20+</span>
                  <span className="text-xs uppercase tracking-widest text-theme-muted">Jaar Medische Ervaring</span>
                </div>
                <div>
                  <span className="block text-3xl font-display text-primary">100%</span>
                  <span className="text-xs uppercase tracking-widest text-theme-muted">Natuurlijk Resultaat</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-theme-surface relative overflow-hidden border-t border-theme-border">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-primary/5 -skew-x-12"></div>
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-display text-4xl text-theme-text mb-8">Klaar voor een verfijnde look?</h2>
          <p className="text-theme-muted text-lg font-light mb-12">
            Plan een vrijblijvende consultatie in met Dr. An De Cuyper om uw wensen te bespreken.
          </p>
          <a 
            href={settings.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary text-black px-12 py-5 text-sm font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all shadow-xl shadow-primary/20 inline-block"
          >
            Nu Afspraak Boeken
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
